import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  Alert,
  alpha,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid'
import {
  Business,
  Add,
  MoreVert,
  Edit,
  ContentCopy,
  CheckCircle,
  AutoAwesome,
  Email,
  ChevronRight,
  Block
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { organisationsService, CreateOrganisationRequest, UpdateOrganisationRequest } from '../../../../shared/services/OrganisationsService'

interface Organization {
  id: string
  companyName: string
  contactEmail: string
  supportEmail: string
  departmentCount: number
  openTickets: number
  pendingTickets: number
  closedTickets: number
  status: 'Active' | 'Inactive'
  isCurrentUserOrg: boolean
  tenantSlug: string
}

interface OrganizationFormData {
  name: string
  email: string
  description?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCounty?: string
  postalCode?: string
  country?: string
}

const organizationSchema = yup.object({
  name: yup.string().required('Organisation name is required').min(2, 'At least 2 characters'),
  email: yup.string().email('Invalid email address').required('Contact email is required'),
  description: yup.string(),
  addressLine1: yup.string(),
  addressLine2: yup.string(),
  city: yup.string(),
  stateCounty: yup.string(),
  postalCode: yup.string(),
  country: yup.string()
})

function OrganizationsNew() {
  const theme = useTheme()
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null)
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<OrganizationFormData>({
    resolver: yupResolver(organizationSchema)
  })

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      setError(null)

      const data = await organisationsService.getOrganisations()

      if (!data || !Array.isArray(data)) {
        console.error('Invalid data received from getOrganisations:', data)
        setOrganizations([])
        setError('Failed to load organisations - invalid data received')
        return
      }

      const mappedOrganizations: Organization[] = data.map(org => ({
        id: org.customerId,
        companyName: org.companyName,
        contactEmail: org.contactEmail,
        supportEmail: org.emailAddress || '',
        departmentCount: org.departmentCount || 0,
        openTickets: org.openTicketCount || 0,
        pendingTickets: org.pendingTicketCount || 0,
        closedTickets: org.closedTicketCount || 0,
        status: org.isActive ? 'Active' : 'Inactive',
        isCurrentUserOrg: org.isCurrentUserOrganization || false,
        tenantSlug: org.tenantSlug
      }))

      // Sort organizations: tenant org first, then alphabetically
      const sorted = mappedOrganizations.sort((a, b) => {
        if (a.isCurrentUserOrg && !b.isCurrentUserOrg) return -1
        if (!a.isCurrentUserOrg && b.isCurrentUserOrg) return 1
        return a.companyName.localeCompare(b.companyName)
      })

      setOrganizations(sorted)
    } catch (error: any) {
      console.error('Failed to load organizations:', error)
      setError(error.message || 'Failed to load organisations')
      setOrganizations([])
    } finally {
      setLoading(false)
    }
  }

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email)
      setCopiedEmail(email)
      setTimeout(() => setCopiedEmail(null), 2000)
    } catch (err) {
      console.error('Failed to copy email address')
    }
  }

  const handleActionMenu = (event: React.MouseEvent<HTMLElement>, orgId: string) => {
    setActionMenuAnchor(event.currentTarget)
    setSelectedOrgId(orgId)
  }

  const handleActionClose = () => {
    setActionMenuAnchor(null)
    setSelectedOrgId(null)
  }

  const handleDrillDown = (orgId: string) => {
    navigate(`/admin/organizations/${orgId}`)
  }

  const handleCreateClick = () => {
    reset({ name: '', email: '', description: '' })
    setCreateDialogOpen(true)
  }

  const handleEditClick = () => {
    const org = organizations.find(o => o.id === selectedOrgId)
    if (org) {
      setSelectedOrg(org)
      setValue('name', org.companyName)
      setValue('email', org.contactEmail)
      setValue('description', '')
      setEditDialogOpen(true)
    }
    handleActionClose()
  }

  const handleDeleteClick = () => {
    const org = organizations.find(o => o.id === selectedOrgId)
    if (org) {
      setSelectedOrg(org)
      setDeleteDialogOpen(true)
    }
    handleActionClose()
  }

  const onCreateSubmit = async (data: OrganizationFormData) => {
    try {
      setSubmitting(true)
      setError(null)

      const request: CreateOrganisationRequest = {
        companyName: data.name,
        contactEmail: data.email,
        notes: data.description,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        stateCounty: data.stateCounty,
        postalCode: data.postalCode,
        country: data.country
      }

      await organisationsService.createOrganisation(request)
      setSuccessMessage('Organisation created successfully')
      setCreateDialogOpen(false)
      reset()
      await loadOrganizations()

      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err: any) {
      setError(err.message || 'Failed to create organisation')
    } finally {
      setSubmitting(false)
    }
  }

  const onEditSubmit = async (data: OrganizationFormData) => {
    if (!selectedOrg) return

    try {
      setSubmitting(true)
      setError(null)

      const request: UpdateOrganisationRequest = {
        companyName: data.name,
        contactEmail: data.email,
        accountStatus: selectedOrg.status,
        billingPlan: 'Standard',
        maxAgents: 10,
        monthlyAmount: 0,
        isActive: selectedOrg.status === 'Active',
        notes: data.description,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        stateCounty: data.stateCounty,
        postalCode: data.postalCode,
        country: data.country
      }

      await organisationsService.updateOrganisation(selectedOrg.id, request)
      setSuccessMessage('Organisation updated successfully')
      setEditDialogOpen(false)
      reset()
      setSelectedOrg(null)
      await loadOrganizations()

      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err: any) {
      setError(err.message || 'Failed to update organisation')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedOrg) return

    try {
      setSubmitting(true)
      setError(null)

      await organisationsService.deactivateOrganisation(selectedOrg.id)
      setSuccessMessage('Organisation deactivated successfully')
      setDeleteDialogOpen(false)
      setSelectedOrg(null)
      await loadOrganizations()

      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate organisation')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDialogClose = () => {
    setCreateDialogOpen(false)
    setEditDialogOpen(false)
    setDeleteDialogOpen(false)
    setSelectedOrg(null)
    reset()
  }

  const columns: GridColDef[] = [
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            cursor: 'pointer',
            '&:hover .company-name': {
              color: theme.palette.primary.main,
              textDecoration: 'underline'
            }
          }}
          onClick={() => handleDrillDown(params.row.id)}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: params.row.isCurrentUserOrg
                ? theme.palette.primary.main
                : theme.palette.grey[400],
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Business fontSize="small" />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              className="company-name"
              variant="body2"
              noWrap
              sx={{ fontWeight: 600 }}
            >
              {params.row.companyName}
            </Typography>
            {params.row.isCurrentUserOrg && (
              <Chip
                label="Your Organisation"
                size="small"
                color="primary"
                sx={{ height: 18, mt: 0.5, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
      )
    },
    {
      field: 'supportEmail',
      headerName: 'Support Ticket Email',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <AutoAwesome fontSize="small" sx={{ color: theme.palette.success.main, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.813rem',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {params.row.supportEmail}
          </Typography>
          <Tooltip title={copiedEmail === params.row.supportEmail ? 'Copied!' : 'Copy'}>
            <IconButton
              size="small"
              onClick={() => handleCopyEmail(params.row.supportEmail)}
              sx={{ flexShrink: 0 }}
            >
              {copiedEmail === params.row.supportEmail ? (
                <CheckCircle fontSize="small" color="success" />
              ) : (
                <ContentCopy fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    {
      field: 'contactEmail',
      headerName: 'Main Contact Email',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email fontSize="small" sx={{ color: theme.palette.grey[600] }} />
          <Typography variant="body2" noWrap>
            {params.row.contactEmail}
          </Typography>
        </Box>
      )
    },
    {
      field: 'departmentCount',
      headerName: 'Departments/Sites',
      type: 'number',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Chip
          label={params.row.departmentCount}
          size="small"
          variant="outlined"
          onClick={() => params.row.departmentCount > 0 && handleDrillDown(params.row.id)}
          sx={{
            cursor: params.row.departmentCount > 0 ? 'pointer' : 'default',
            '&:hover': params.row.departmentCount > 0 ? {
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            } : {}
          }}
        />
      )
    },
    {
      field: 'openTickets',
      headerName: 'Open Tickets',
      type: 'number',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Chip
          label={params.row.openTickets}
          size="small"
          color={params.row.openTickets > 0 ? 'error' : 'default'}
          sx={{ minWidth: 45 }}
        />
      )
    },
    {
      field: 'pendingTickets',
      headerName: 'Pending Tickets',
      type: 'number',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Chip
          label={params.row.pendingTickets}
          size="small"
          color={params.row.pendingTickets > 0 ? 'warning' : 'default'}
          sx={{ minWidth: 45 }}
        />
      )
    },
    {
      field: 'closedTickets',
      headerName: 'Closed Tickets',
      type: 'number',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Chip
          label={params.row.closedTickets}
          size="small"
          color="success"
          variant="outlined"
          sx={{ minWidth: 45 }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<Organization>) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleDrillDown(params.row.id)}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) => handleActionMenu(e, params.row.id)}
          >
            <MoreVert />
          </IconButton>
        </Box>
      )
    }
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color: theme.palette.primary.main }} />
            Organisation Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage customer organisations and their sites
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateClick}
          sx={{ px: 3 }}
        >
          Add Organisation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={organizations}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          disableRowSelectionOnClick
          disableColumnSorting
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04)
              }
            },
            '& .MuiDataGrid-row.current-user-org': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderLeft: `3px solid ${theme.palette.primary.main}`
            },
            '& .MuiDataGrid-cell': {
              py: 1
            }
          }}
          getRowClassName={(params) =>
            params.row.isCurrentUserOrg ? 'current-user-org' : ''
          }
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={handleEditClick}>
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit Organisation
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <Block sx={{ mr: 1 }} fontSize="small" color="error" />
          Deactivate Organisation
        </MenuItem>
      </Menu>

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onCreateSubmit)}>
          <DialogTitle>Add New Organisation</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organisation Name"
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1 (Optional)"
                  {...register('addressLine1')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  {...register('addressLine2')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City (Optional)"
                  {...register('city')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/County (Optional)"
                  {...register('stateCounty')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code (Optional)"
                  {...register('postalCode')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country (Optional)"
                  {...register('country')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  multiline
                  rows={3}
                  {...register('description')}
                  disabled={submitting}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Organisation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onEditSubmit)}>
          <DialogTitle>Edit Organisation</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organisation Name"
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1 (Optional)"
                  {...register('addressLine1')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  {...register('addressLine2')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City (Optional)"
                  {...register('city')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/County (Optional)"
                  {...register('stateCounty')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code (Optional)"
                  {...register('postalCode')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country (Optional)"
                  {...register('country')}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  multiline
                  rows={3}
                  {...register('description')}
                  disabled={submitting}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Updating...' : 'Update Organisation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Organisation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate <strong>{selectedOrg?.companyName}</strong>?
            This will prevent users in this organisation from accessing the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={submitting}>
            {submitting ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OrganizationsNew
